const convertDashesToSlashes = (date) => {
    return date ? date.replace(/-/g, '/') : '';
};

const isDateObject = (date) => {
    return date instanceof Date ? !isNaN(date.getTime()) : false;
};

const isDateString = (date) => {
    return typeof date === 'string' ? isDateObject(new Date(date)) : false;
};

/**
 * If given a `Date` object, makes sure that the date occurs on or after
 * 1970-01-01. Otherwise, it makes sure that the string passed in
 * represents a date.
 *
 * @param  {(Date|string)}  date The `Date` object or string to validate
 * @return {boolean}
 */
const isValidDate = (date) => {
    return (isDateObject(date) && +date >= 0) || isDateString(date);
};

/**
 * Returns `true` when both arguments can be turned into valid `Date`s
 *
 * @param  {string} firstDateStr  A string representation of a date
 * @param  {string} secondDateStr A string representation of a date
 * @return {boolean}
 */
const areBothDatesValid = (firstDateStr, secondDateStr) => {
    return isValidDate(new Date(firstDateStr)) && isValidDate(new Date(secondDateStr));
};

// Take in a string and count the number of times each letter appears
// Return an object like {a: 2, b: 1, c: 10}
const getFrequency = (str) => {
    const result = {};

    for (let i = 0; i < str.length; i++) {
        const char = str.charAt(i);
        result[char] = result[char] ? result[char] + 1 : 1;
    }

    return result;
};

// Takes in date obj and character frequency obj
// Maps formatted strings onto date obj
// Returns object like {d:'01' m:'08' y: '1987'}
/* eslint-disable complexity */
const formatDateParts = (date, charFreqency, useLocal) => {
    let day = useLocal ? `${date.getDate()}` : `${date.getUTCDate()}`;
    let month = useLocal ? `${date.getMonth() + 1}` : `${date.getUTCMonth() + 1}`;
    let year = useLocal ? `${date.getFullYear()}` : `${date.getUTCFullYear()}`;
    year = year.substr(year.length - charFreqency.y);

    day = day.length === 1 && charFreqency.d === 2 ? `0${day}` : day;
    month = month.length === 1 && charFreqency.m === 2 ? `0${month}` : month;

    return {
        d: day,
        m: month,
        y: year
    };
};
/* eslint-enable complexity */

// Return a date in YYYY-MM-DD format
// Optional 'format' param (eg. 'dd-mm-yy') overrides default
// Optional 'useLocal' bool sets date relative to the users local timezone rather than utc time
const formatDateObj = (aDate, format, useLocal) => {
    const targetFormat = format.toLowerCase();
    const charFreqency = getFrequency(targetFormat);
    const formattedDateParts = formatDateParts(aDate, charFreqency, useLocal);
    let formattedDate = targetFormat;

    for (let key in formattedDateParts) {
        /* istanbul ignore next */
        if ({}.hasOwnProperty.call(formattedDateParts, key)) {
            let targetStr = key.repeat(charFreqency[key]);
            let formattedStr = formattedDateParts[key];
            formattedDate = formattedDate.replace(targetStr, formattedStr);
        }
    }

    return formattedDate;
};

const formatDateString = (aDate, format) => {
    return formatDateObj(new Date(convertDashesToSlashes(aDate)), format, true);
};

const formatDate = (aDate, format, useLocal) => {
    const targetFormat = format || 'YYYY-MM-DD';
    const targetLocal = useLocal || false;

    return (
        (isDateString(aDate) && formatDateString(aDate, targetFormat)) ||
        (isDateObject(aDate) && formatDateObj(aDate, targetFormat, targetLocal)) ||
        ''
    );
};

const formatDateStringToLocaleString = (date, locale, opts) => {
    if (!date || !locale) {
        return null;
    }

    const options = opts || {month: 'long', day: 'numeric'};
    const dateObj = new Date(convertDashesToSlashes(date));
    const formattedLocale = locale.replace('_', '-');
    return dateObj.toLocaleString(formattedLocale, options);
};

const isTodayDateObj = (date) => {
    return date.setHours(0, 0, 0, 0) === new Date().setHours(0, 0, 0, 0);
};

const isTodayDateStr = (date) => {
    return formatDate(new Date(), 'YYYY-MM-DD', true) === date;
};

const isToday = (date) => {
    const dateStrResult = isDateString(date) ? isTodayDateStr(date) : null;
    const dateObjResult = isDateObject(date) ? isTodayDateObj(date) : null;

    return dateStrResult || dateObjResult || false;
};

const isEqual = (date1, date2) => {
    return date1 && date2 ? date1 === date2 : false;
};

const isBeforeDateObj = (date1, date2) => {
    return +date1 < +date2;
};

const isBeforeDateStr = (date1, date2) => {
    return isBeforeDateObj(new Date(date1), new Date(date2));
};

const isBefore = (date1, date2) => {
    const dateStrResult =
        isDateString(date1) && isDateString(date2) ? isBeforeDateStr(date1, date2) : null;
    const dateObjResult =
        isDateObject(date1) && isDateObject(date2) ? isBeforeDateObj(date1, date2) : null;

    return dateStrResult || dateObjResult || false;
};

const isFutureDateObj = (date) => {
    const now = new Date();
    return date.setHours(0, 0, 0, 0) > now.setHours(0, 0, 0, 0);
};

const isFutureDateStr = (date) => {
    return isFutureDateObj(new Date(convertDashesToSlashes(date)));
};

const isFuture = (date) => {
    const dateStrResult = isDateString(date) ? isFutureDateStr(date) : null;
    const dateObjResult = isDateObject(date) ? isFutureDateObj(date) : null;

    return dateStrResult || dateObjResult || false;
};

const isPastDateObj = (date) => {
    const now = new Date();
    return date.setHours(0, 0, 0, 0) < now.setHours(0, 0, 0, 0);
};

const isPastDateStr = (date) => {
    return isPastDateObj(new Date(convertDashesToSlashes(date)));
};

const isPast = (date) => {
    const dateStrResult = isDateString(date) ? isPastDateStr(date) : null;
    const dateObjResult = isDateObject(date) ? isPastDateObj(date) : null;

    return dateStrResult || dateObjResult || false;
};

const subtractDaysFromDate = (date, days) => {
    if (!isValidDate(date)) {
        return null;
    }

    const dateIsString = isDateString(date);
    const dateInstance = dateIsString ? new Date(convertDashesToSlashes(date)) : date;
    const result = new Date(
        dateInstance.getFullYear(),
        dateInstance.getMonth(),
        dateInstance.getDate() - days,
        dateInstance.getHours(),
        dateInstance.getMinutes(),
        dateInstance.getSeconds(),
        dateInstance.getMilliseconds()
    );

    return dateIsString ? formatDate(result, 'YYYY-MM-DD', true) : result;
};

const addDaysToDate = (date, days) => {
    if (!isValidDate(date)) {
        return null;
    }

    const dateIsString = isDateString(date);
    const dateInstance = dateIsString ? new Date(convertDashesToSlashes(date)) : date;
    const result = new Date(
        dateInstance.getFullYear(),
        dateInstance.getMonth(),
        dateInstance.getDate() + days,
        dateInstance.getHours(),
        dateInstance.getMinutes(),
        dateInstance.getSeconds(),
        dateInstance.getMilliseconds()
    );

    return dateIsString ? formatDate(result, 'YYYY-MM-DD', true) : result;
};

/**
 * Given two string representations of dates, it returns the date
 * string belonging to the earliest date between the two.
 *
 * @param  {string} firstDateStr  A string representation of a `Date`
 * @param  {string} secondDateStr A string representation of a `Date`
 * @return {string}               The string belonging to the earlier of the two dates, or 1970-01-01 when both dates are invalid.
 */
const min = (firstDateStr = '1970-01-01', secondDateStr = '1970-01-01') => {
    const firstDate = new Date(firstDateStr);
    const secondDate = new Date(secondDateStr);

    if (!areBothDatesValid(firstDate, secondDate)) {
        if (!isValidDate(firstDate) && isValidDate(secondDate)) {
            return secondDateStr;
        } else if (isValidDate(firstDate) && !isValidDate(secondDate)) {
            return firstDateStr;
        }

        return '1970-01-01';
    }

    const closestDate = Math.min(firstDate, secondDate);

    return closestDate === +firstDate ? firstDateStr : secondDateStr;
};

class DateUtils {
    constructor() {
        this.date = new Date();
        this.today = new Date();
        this.tomorrow = addDaysToDate(new Date(), 1);
        this.yesterday = subtractDaysFromDate(new Date(), 1);
        this.year = this.date.getFullYear().toString();
        this.month = this.date.getMonth();
        this.day = this.date.getDate();
        this.dayName = this.date.toLocaleDateString('en-us', {weekday: 'long'});
        this.monthName = this.date.toLocaleDateString('en-us', {month: 'long'});
        this.nextMonthDate = new Date(new Date().setMonth(new Date().getMonth() + 1));
        this.nextMonthName = this.nextMonthDate.toLocaleDateString('en-us', {month: 'long'});
        this.nextMonthMonthNum = this.nextMonthDate.toLocaleDateString('en-us', {month: '2-digit'});
        this.nextMonthYearNum = this.nextMonthDate.toLocaleDateString('en-us', {year: 'numeric'});
        this.nextnextMonthDate = new Date(new Date().setMonth(new Date().getMonth() + 2));
        this.nextnextMonthName = this.nextnextMonthDate.toLocaleDateString('en-us', {
            month: 'long'
        });
        this.nextnextMonthMonthNum = this.nextnextMonthDate.toLocaleDateString('en-us', {
            month: '2-digit'
        });
        this.nextnextMonthYearNum = this.nextnextMonthDate.toLocaleDateString('en-us', {
            year: 'numeric'
        });
        this.todayFormatted = formatDate(new Date(), 'YYYY-MM-DD', true);
    }
}

class FormattedDateUtils {
    constructor() {
        this.today = formatDate(new Date(), 'YYYY-MM-DD', true);
        this.tomorrow = formatDate(addDaysToDate(new Date(), 1), 'YYYY-MM-DD', true);
        this.yesterday = formatDate(subtractDaysFromDate(new Date(), 1), 'YYYY-MM-DD', true);
        this.todayPlusTwoDays = formatDate(addDaysToDate(new Date(), 2), 'YYYY-MM-DD', true);
        this.todayPlusThreeDays = formatDate(addDaysToDate(new Date(), 3), 'YYYY-MM-DD', true);
        this.todayPlusFourDays = formatDate(addDaysToDate(new Date(), 4), 'YYYY-MM-DD', true);
        this.todayPlusFiveDays = formatDate(addDaysToDate(new Date(), 5), 'YYYY-MM-DD', true);
        this.todayPlusThirtyDays = formatDate(addDaysToDate(new Date(), 30), 'YYYY-MM-DD', true);
        this.todayPlusSixtyDays = formatDate(addDaysToDate(new Date(), 60), 'YYYY-MM-DD', true);
        this.todayPlusOneMonth = formatDate(
            new Date(new Date().setMonth(new Date().getMonth() + 1)),
            'YYYY-MM-DD',
            true
        );
        this.todayPlusTwoMonths = formatDate(
            new Date(new Date().setMonth(new Date().getMonth() + 2)),
            'YYYY-MM-DD',
            true
        );
    }
}

module.exports = {
    DateUtils,
    FormattedDateUtils,
    formatDate,
    formatDateStringToLocaleString,
    subtractDaysFromDate,
    addDaysToDate,
    isDateObject,
    isDateString,
    isBefore,
    isFuture,
    isPast,
    isToday,
    isValidDate,
    areBothDatesValid,
    isEqual,
    convertDashesToSlashes,
    min
};
